import { useCheckTypes } from '../lib/checkTypes';
import { useEffect, useState } from 'react';
import { Banknote, Check, Mailbox, Pause, Play, Receipt, Send, Trash2 } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { CheckConfirmDialog, type CheckConfirm } from './CheckConfirmDialog';
import { useI18n } from '../lib/i18n';
import { CheckClocks } from './CheckClocks';
import { formatUsd, localeUI } from '../lib/format';
import { uploadFile } from '../lib/upload';
import { FileDrop } from './FileDrop';
import { PaymentsTimeline } from './PaymentsTimeline';
import { PaymentProgressBar, type PaymentMoney } from './PaymentProgressBar';
import { PaymentReconciliation, type Reconciliation } from './PaymentReconciliation';
import { ContractorAuthBox } from './ContractorAuthBox';
import { PaymentSummaryEmail } from './PaymentSummaryEmail';
import { ClaimChat } from './ClaimChat';
import { MortgageBox } from './MortgageBox';
import { HomeownerBankBox } from './HomeownerBankBox';

/**
 * Módulos de PAGOS del panel del claim (mismos endpoints que el claim abierto): Checks
 * (registrar/tipo/borrar), Claim invoices (+ Hold payment del claim) y Check sent dates
 * (ACV/Depreciation/Supplement/Bounced/Mortgage con "Check sent"/"Arrived"). Las acciones de
 * PAGO con gates de compliance (pagar invoice) siguen viviendo en el claim completo.
 */
type Check = { id: string; amount_cents: string | number; status: string; payable_to: string; payable_to_name: string | null; check_number: string | null; deposited_at: string | null; cleared_at: string | null; check_type: string | null };
type Invoice = { id: string; invoice_number_global: string | null; invoice_number_per_claim: number | null; status: string; payout_cents: number | null; fee_billed_this_invoice_cents: number | null; issued_at: string | null };
type CheckDateRow = { id: string; stage: string; is_extra: boolean; sent_on: string; received_on: string | null; received_by: string | null; sent_by: string | null; amount_cents: number | null; /** Correcciones previas: desde 1 el backend exige razón. */ edit_count?: number;
  /** El invoice que facturó ESTE cheque (solo si el monto lo identifica sin ambigüedad). */
  invoice_id?: string | null; invoice_number?: string | null; payment_id?: string | null;
  paid_at?: string | null; has_proof?: boolean; has_invoice_pdf?: boolean };

/**
 * El MISMO indicador que la tabla de ClaimDetail: si este cheque ya se facturó y se cobró, con el
 * invoice y el comprobante a un clic. Va en las dos vistas a propósito — la pantalla completa y el
 * drawer muestran la misma fila, y una que diga "pagado" y la otra no es la forma más rápida de que
 * dejen de creerle a las dos.
 */
function ChequeFacturado({ row }: { row: CheckDateRow }) {
  const { t } = useI18n();
  const [err, setErr] = useState<string | null>(null);
  if (!row.invoice_id) return null;
  async function abrir(url: string) {
    setErr(null);
    try {
      const r = await api.get<{ url: string }>(url);
      window.open(r.url, '_blank', 'noopener');
    } catch { setErr(t('cd.chkPaidOpenErr')); }
  }
  const pagado = Boolean(row.paid_at);
  return (
    <span style={{ display: 'inline-flex', gap: 5, alignItems: 'center', flexWrap: 'wrap' }}>
      <span className="badge" style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)',
        background: pagado ? 'var(--green-soft)' : 'var(--orange-soft)',
        color: pagado ? 'var(--green)' : 'var(--orange)' }}>
        {pagado ? `✓ ${t('cd.chkPaid')}` : `⏳ ${t('cd.chkInvoicedNotPaid')}`}
      </span>
      {row.has_invoice_pdf && (
        <button className="btn ghost sm" style={{ fontSize: 'var(--fs-2xs)' }}
          onClick={() => void abrir(`/api/invoices/${row.invoice_id}/pdf-url`)}>
          {row.invoice_number ?? t('cd.chkInvoice')}
        </button>
      )}
      {pagado && row.has_proof && row.payment_id && (
        <button className="btn ghost sm" style={{ fontSize: 'var(--fs-2xs)' }}
          onClick={() => void abrir(`/api/payments/${row.payment_id}/proof-url`)}>
          {t('cd.viewProof')}
        </button>
      )}
      {err && <span style={{ color: 'var(--red)', fontSize: 'var(--fs-2xs)' }}>{err}</span>}
    </span>
  );
}

const CHECK_STAGES: { stage: string; key: string }[] = [
  { stage: 'acv', key: 'cd.stageAcv' }, { stage: 'depreciation', key: 'cd.stageDepreciation' },
  { stage: 'supplement', key: 'cd.stageSupplement' }, { stage: 'bounced', key: 'cd.stageBounced' }, { stage: 'mortgage', key: 'cd.stageMortgage' },
];
const today = () => new Date().toISOString().slice(0, 10);
const fmtD = (d: string | null | undefined) => (d ? new Date(`${d}T12:00:00`).toLocaleDateString(localeUI()) : '—');

function SecTitle({ children, right }: { children: React.ReactNode; right?: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
      <span className="muted" style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-bold)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{children}</span>
      <span style={{ flex: 1 }} />
      {right}
    </div>
  );
}

export function ClaimPaymentsFull({ claimId, paymentHold, holdNote, onHoldChanged, contractorName }: { claimId: string; paymentHold?: boolean; holdNote?: string | null; onHoldChanged?: () => void; contractorName?: string | null }) {
  const { t } = useI18n();
  const tiposDeCheque = useCheckTypes(t);
  const mono: React.CSSProperties = { fontFamily: 'var(--font-mono)', fontWeight: 600 };
  const [checks, setChecks] = useState<Check[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [dates, setDates] = useState<CheckDateRow[]>([]);
  const [money, setMoney] = useState<PaymentMoney | null>(null);
  const [recon, setRecon] = useState<Reconciliation | null>(null);
  const [tlKey, setTlKey] = useState(0); // remonta el tracker al mandar un resumen
  const [amt, setAmt] = useState('');
  const [payable, setPayable] = useState('proactive');
  const [recv, setRecv] = useState<Record<string, string>>({});
  const [sent, setSent] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [hold, setHold] = useState(!!paymentHold);
  useEffect(() => { setHold(!!paymentHold); }, [paymentHold]);

  // Datos del MORTGAGE (banco, loan #, PO box) — el drawer no los tenía y son parte de la
  // tarjeta del claim (pedido 2026-08-10). Se recargan al guardar desde el propio box.
  type MtgClaim = { mortgage_company?: string | null; mortgage_name?: string | null; mortgage_loan_number?: string | null; mortgage_po_box?: string | null; mortgage_check_sent_on?: string | null };
  const [mtgClaim, setMtgClaim] = useState<MtgClaim | null>(null);
  const loadMtg = () => api.get<MtgClaim>(`/api/claims/${claimId}`).then(setMtgClaim).catch(() => {});
  const loadChecks = () => api.get<{ checks: Check[] }>(`/api/checks?claimId=${claimId}`).then((r) => setChecks(r.checks)).catch(() => {});
  const loadInvoices = () => api.get<{ invoices: Invoice[] }>(`/api/claims/${claimId}/invoices`).then((r) => setInvoices(r.invoices)).catch(() => {});
  const loadDates = () => api.get<{ dates: CheckDateRow[] }>(`/api/claims/${claimId}/check-dates`).then((r) => setDates(r.dates)).catch(() => {});
  const loadMoney = () => api.get<PaymentMoney>(`/api/claims/${claimId}/money`).then(setMoney).catch(() => {});
  const loadRecon = () => api.get<Reconciliation>(`/api/claims/${claimId}/reconciliation`).then(setRecon).catch(() => {});
  useEffect(() => { loadChecks(); loadInvoices(); loadDates(); loadMoney(); loadRecon(); loadMtg(); /* eslint-disable-next-line */ }, [claimId]);

  async function registerCheck() {
    const cents = Math.round(parseFloat(amt || '0') * 100);
    if (!(cents > 0)) return;
    setBusy(true);
    try { await api.post('/api/checks', { claimId, amountCents: cents, payableTo: payable }); setAmt(''); await loadChecks(); }
    catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); } finally { setBusy(false); }
  }
  const [scanBusy, setScanBusy] = useState(false);
  // Drag & drop de la FOTO/PDF del cheque → la IA lee monto / #cheque / beneficiario / TIPO y
  // crea el cheque en este claim. Para cheques pasados: después se ajusta depósito/disponible/status.
  async function scanCheckFile(file: File) {
    setScanBusy(true);
    try {
      const key = await uploadFile(file, 'checks');
      await api.post('/api/checks/scan', { claimId, fileKey: key, fileName: file.name, contentType: file.type || 'image/jpeg' });
      await loadChecks();
    } catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); } finally { setScanBusy(false); }
  }
  async function setType(id: string, type: string) {
    try { await api.post(`/api/checks/${id}/type`, { type: type || null }); await loadChecks(); } catch { /* */ }
  }
  async function delCheck(id: string) {
    if (!window.confirm(t('cd.deleteCheckConfirm') || '¿Borrar cheque?')) return;
    try { await api.del(`/api/checks/${id}`); await loadChecks(); } catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); }
  }
  /** Etapa esperando confirmación: desde la mig 0376 la fecha no se guarda sin decir con quién. */
  const [confirmando, setConfirmando] = useState<string | null>(null);
  async function checkSent(stage: string) { setConfirmando(stage); }
  async function checkSentConfirmado(stage: string, conf: CheckConfirm) {
    const date = sent[stage] || dates.find((d) => d.stage === stage && !d.is_extra)?.sent_on || today();
    try {
      await api.post(`/api/claims/${claimId}/check-sent-date`, { stage, date, ...conf });
      setConfirmando(null);
      await loadDates();
    } catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); }
  }
  async function checkReceived(stage: string, date: string | null) {
    try { await api.post(`/api/claims/${claimId}/check-received-date`, { stage, date }); await loadDates(); } catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); }
  }
  async function toggleHold() {
    try {
      if (hold) { await api.del(`/api/claims/${claimId}/payment-hold`); setHold(false); }
      else { const note = window.prompt(t('hold.note') || 'Motivo del hold:') ?? ''; await api.post(`/api/claims/${claimId}/payment-hold`, { note: note.trim() }); setHold(true); }
      onHoldChanged?.();
    } catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); }
  }

  const totalReceived = checks.reduce((s, c) => s + Number(c.amount_cents), 0);

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      {/* AUTORIZACIÓN DEL HOMEOWNER, ARRIBA DE TODO.
          Pedido del dueño: que sea fácil de ENCONTRAR y de ESCUCHAR. En el drawer no existía —
          había que salir al claim completo para oír la grabación—, y es lo primero que se necesita
          para poder pagarle al contratista: si no está, el pago no sale. Por eso va antes que los
          invoices y no al final. */}
      <ContractorAuthBox claimId={claimId} contractorName={contractorName} compacto />

      {/* INVOICES + Hold payment del claim */}
      <div className="card" style={hold ? { borderLeft: '4px solid var(--red)' } : undefined}>
        <SecTitle right={<button className="btn ghost sm" style={hold ? { color: 'var(--red)', fontWeight: 700 } : { fontWeight: 700 }} onClick={toggleHold}>{hold ? <><Play size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('hold.release')}</> : <><Pause size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('cd.holdPayment') || 'Hold payment'}</>}</button>}>
          <Receipt size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('cd.claimInvoices') || 'Claim invoices'}
          {hold && <span className="badge red" style={{ marginLeft: 8, fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)' }} title={holdNote ?? ''}><Pause size={10} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('hold.holdedTag')}</span>}
        </SecTitle>
        {/* Misma barra de pago animada del tab Payments: recibido→esperado + cuánto falta.
            Se muestra SIEMPRE (aunque el claim aún no tenga estimado: sale en $0). */}
        <div style={{ marginBottom: 12 }}><PaymentProgressBar money={money} compact /></div>
        {invoices.length === 0 ? <div className="muted" style={{ fontSize: 'var(--fs-xs)' }}>—</div> : (
          <div style={{ display: 'grid', gap: 6 }}>
            {invoices.map((i) => (
              <div key={i.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--fs-sm)', borderBottom: '1px solid var(--line)', paddingBottom: 6, flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 'var(--fw-semibold)', fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)' }}>{i.invoice_number_global || `#${i.invoice_number_per_claim ?? i.id}`}</span>
                <span className={`badge ${i.status === 'paid' ? 'green' : 'gray'}`} style={{ fontSize: 'var(--fs-2xs)' }}>{i.status}</span>
                <span style={{ flex: 1 }} />
                {i.fee_billed_this_invoice_cents != null && <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>fee <span style={mono}>{formatUsd(i.fee_billed_this_invoice_cents)}</span></span>}
                {i.payout_cents != null && <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>payout <span style={{ ...mono, color: 'var(--ink)' }}>{formatUsd(i.payout_cents)}</span></span>}
                <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{fmtD(i.issued_at?.slice(0, 10))}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* LOS DOS RELOJES DEL CHEQUE por etapa, justo debajo del resumen (pedido 2026-08-19). */}
      <CheckClocks claimId={claimId} />
      {/* Reconciliación del claim (mismo módulo del tab Payments) — ANTES del tracking.
          Siempre visible: si el claim no tiene estimado todavía, sale en $0 con el aviso. */}
      {recon && <div className="card"><PaymentReconciliation recon={recon} linkToTracker claimId={claimId} onEdited={loadRecon} /></div>}
      {/* Resumen de pago por email (IA, EN/ES) — justo debajo del cuadre, para cuando el HO o el
          contratista llaman pidiendo aclaración. Al enviarlo se refresca el tracker. */}
      <PaymentSummaryEmail claimId={claimId} onSent={() => setTlKey((k) => k + 1)} />
      {/* Timeline del proceso de dinero: cheque → invoice → autorización → pago */}
      <div className="card">
        <PaymentsTimeline key={tlKey} claimId={claimId} />
      </div>
      {/* Updates del depto de Payments — justo después del tracker (pedido del dueño). */}
      <ClaimChat claimId={claimId} channel="payments" title={t('cdw.financeUpdates')} />
      {/* CHECK SENT DATES: por etapa, marcar enviado + llegó. */}
      <div className="card">
        <SecTitle><Mailbox size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('cd.checkDatesTitle')}</SecTitle>
        <div className="muted" style={{ fontSize: 'var(--fs-2xs)', marginBottom: 8 }}>{t('cd.checkDatesHelp')}</div>
        <div style={{ display: 'grid', gap: 8 }}>
          {CHECK_STAGES.map((s) => {
            const row = dates.find((d) => d.stage === s.stage && !d.is_extra);
            const sentOn = row?.sent_on ? row.sent_on.slice(0, 10) : null;
            const recvOn = row?.received_on ? row.received_on.slice(0, 10) : null;
            const days = sentOn ? (recvOn ? Math.max(0, Math.round((new Date(`${recvOn}T12:00:00`).getTime() - new Date(`${sentOn}T12:00:00`).getTime()) / 86400000)) : Math.max(0, Math.round((Date.now() - new Date(`${sentOn}T12:00:00`).getTime()) / 86400000))) : null;
            return (
              <div key={s.stage} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', borderBottom: '1px solid var(--line)', paddingBottom: 8 }}>
                <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)', minWidth: 120 }}>{t(s.key)}</span>
                {sentOn
                  ? <span className="badge" style={{ fontSize: 'var(--fs-2xs)', background: 'var(--accent-soft)', color: 'var(--accent)', fontWeight: 'var(--fw-bold)' }}><Send size={10} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {fmtD(sentOn)}{days != null ? ` · ${days}d` : ''}</span>
                  : <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>—</span>}
                {row && <ChequeFacturado row={row} />}
                <span style={{ flex: 1 }} />
                {!sentOn && (
                  <>
                    <input className="input sm" type="date" style={{ width: 140 }} value={sent[s.stage] ?? ''} onChange={(e) => setSent((p) => ({ ...p, [s.stage]: e.target.value }))} />
                    <button className="btn sm" style={{ background: 'var(--accent)', color: '#fff', borderColor: 'transparent' }} onClick={() => checkSent(s.stage)}><Check size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('cd.checkSent') || 'Check sent'}</button>
                  </>
                )}
                {/* La fecha no se guarda sin decir con quién se confirmó (mig 0376). */}
                {confirmando === s.stage && (
                  <div style={{ flexBasis: '100%' }}>
                    <CheckConfirmDialog
                      needsReason={(dates.find((d) => d.stage === s.stage && !d.is_extra)?.edit_count ?? 0) >= 1}
                      onCancel={() => setConfirmando(null)}
                      onConfirm={(c) => checkSentConfirmado(s.stage, c)}
                    />
                  </div>
                )}
                {sentOn && (recvOn
                  ? <span className="badge" style={{ fontSize: 'var(--fs-2xs)', background: 'var(--green-soft)', color: 'var(--green)', fontWeight: 'var(--fw-bold)' }}><Check size={10} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {fmtD(recvOn)}<button className="btn ghost sm" style={{ padding: '0 5px', marginLeft: 4, color: 'var(--orange)', fontWeight: 'var(--fw-bold)', border: '1px solid var(--orange)', borderRadius: 6, background: '#fff' }} onClick={() => { if (confirm(t('cd.checkArrivedUndoConfirm'))) checkReceived(s.stage, null); }} title={t('cd.checkArrivedUndo')}>↩ {t('cd.checkArrivedUndoShort')}</button></span>
                  : (
                    <>
                      <input className="input sm" type="date" style={{ width: 140 }} value={recv[s.stage] ?? ''} onChange={(e) => setRecv((p) => ({ ...p, [s.stage]: e.target.value }))} />
                      <button className="btn sm" style={{ background: 'var(--good-fill)', color: 'var(--good-fill-ink)', borderColor: 'transparent' }} onClick={() => checkReceived(s.stage, recv[s.stage] || today())}><Check size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('cd.checkArrived')}</button>
                    </>
                  ))}
              </div>
            );
          })}
        </div>
      </div>
      {/* Datos del mortgage (banco / loan # / PO box), editables desde la tarjeta. */}
      {mtgClaim && <MortgageBox claimId={claimId} claim={mtgClaim} onSaved={loadMtg} />}

      {/* Bank info del HOMEOWNER: dónde depositarle + foto del cheque (pedido 2026-08-10). */}
      <HomeownerBankBox claimId={claimId} />

      {/* CHECKS: registrar + lista */}
      <div className="card">
        <SecTitle><Banknote size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('cdw.checksReceived')}</SecTitle>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
          <input className="input sm" style={{ width: 120 }} placeholder="$ 0.00" value={amt} onChange={(e) => setAmt(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') registerCheck(); }} />
          <select className="input sm" value={payable} onChange={(e) => setPayable(e.target.value)}>
            <option value="proactive">{t('cd.payableProactive')}</option>
            <option value="proactive_ho">{t('cd.payableHo')}</option>
            <option value="proactive_mortgage">{t('cd.payableMortgage')}</option>
          </select>
          <button className="btn sm" disabled={busy || !(parseFloat(amt || '0') > 0)} onClick={registerCheck}>{t('cd.registerCheck')}</button>
        </div>
        {/* Drag & drop de la foto/PDF del cheque → la IA lo lee (monto, #cheque, beneficiario, tipo). */}
        <div style={{ marginBottom: 10 }}>
          <FileDrop onFile={scanCheckFile} accept="application/pdf,image/*" label={t('chk.scanDrop')} busy={scanBusy} busyLabel={t('cd.reading')} compact />
          <div className="muted" style={{ fontSize: 'var(--fs-2xs)', marginTop: 3 }}>{t('chk.scanHint')}</div>
        </div>
        {checks.length === 0 ? <div className="muted" style={{ fontSize: 'var(--fs-xs)' }}>{t('cd.noChecks')}</div> : (
          <div style={{ display: 'grid', gap: 6 }}>
            {checks.map((c) => (
              <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--fs-sm)', borderBottom: '1px solid var(--line)', paddingBottom: 6, flexWrap: 'wrap' }}>
                <span style={{ ...mono, fontSize: 'var(--fs-sm)' }}>{formatUsd(Number(c.amount_cents))}</span>
                {c.check_number && <span className="muted" style={{ fontSize: 'var(--fs-2xs)', fontFamily: 'var(--font-mono)' }}>#{c.check_number}</span>}
                <select className="input sm" style={{ width: 130 }} value={c.check_type || ''} onChange={(e) => setType(c.id, e.target.value)}>
                  <option value="">{t('chk.typeNone')}</option>
                  {tiposDeCheque.map((tp) => <option key={tp.value} value={tp.value}>{tp.label}</option>)}
                  {c.check_type && !tiposDeCheque.some((tp) => tp.value === c.check_type) && <option value={c.check_type}>{c.check_type}</option>}
                </select>
                <span className={`badge ${c.cleared_at ? 'green' : 'gray'}`} style={{ fontSize: 'var(--fs-2xs)' }}>{c.status}</span>
                <span style={{ flex: 1 }} />
                <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{c.deposited_at ? `${t('cdw.deposited')}: ${fmtD(c.deposited_at.slice(0, 10))}` : '—'}</span>
                <button className="btn ghost sm" style={{ color: 'var(--red)', padding: '2px 6px' }} onClick={() => delCheck(c.id)}><Trash2 size={13} strokeWidth={1.75} /></button>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-bold)', paddingTop: 4 }}>
              <span>{t('cd.totalReceived')}</span><span style={mono}>{formatUsd(totalReceived)}</span>
            </div>
          </div>
        )}
      </div>

    </div>
  );
}
